import { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Button from "../../components/button";
import CustomTabs from "../../components/CustomTabs";
import Overlay from "../../components/overlay";
import VehicleLogsComments from "../../components/VehicleLogsComments";
import { getVehicleDetailsByFuelLog } from "../../store/Slices/FuelSlice";
import { images, imagesAlt } from "../../utils/images";
import { activeDetailsIcon, currentSpeedIcon, detailsIcon, lastFuelVolIcon, odoMeterReadingIcon, refuelIcon } from "../../utils/svgs";
import FuelCard from "./FuelCard";
import RefuelingHistory from "./RefuelingHistory";
import SpinnerLoader from "../../utils/SpinnerLoader";

const fuelData = [
    90, 89, 87, 85, 83, 81, 78, 75, 72, 68, 64, 60, 57, 54, 50, 47, 44, 41, 38,
    35, 32, 30, 28, 25,
];

const baseOptions = {
    chart: {
        toolbar: { show: false },
        zoom: { enabled: false },
        foreColor: "#98A2B3",
        fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial",
        parentHeightOffset: 0,
    },

    stroke: { curve: "smooth", width: 2.8 },
    markers: { size: 0 },
    dataLabels: { enabled: false },
    legend: { show: false },

    grid: {
        borderColor: "#EAECF0",
        strokeDashArray: 4,
        padding: {
            left: 0,
            right: 12,
            top: 6,
            bottom: 0,
        },
    },

    xaxis: {
        // categories: ,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
            style: { colors: "#98A2B3", fontSize: "12px" },
            offsetY: 2,
        },
        tooltip: { enabled: false },
    },

    yaxis: {
        min: 0,
        axisBorder: { show: false },
        axisTicks: { show: false },

        labels: {
            style: { colors: "#98A2B3", fontSize: "12px" },
            offsetX: -10,
        },

        title: {
            rotate: -90,
            offsetX: -6,
            offsetY: 0,
            style: {
                color: "#98A2B3",
                fontSize: "12px",
                fontWeight: 400,
            },
        },
    },

    tooltip: { theme: "light" },
};

const VehicleDetails = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { id } = useParams();

    const { getVehicleDetailsByFuelLogData, getVehicleDetailsByFuelLogLoading } = useSelector((state) => state.fuel);

    const [activeTab, setActiveTab] = useState("detail");

    useEffect(() => {
        dispatch(getVehicleDetailsByFuelLog(id));
    }, [])

    if (getVehicleDetailsByFuelLogLoading) {
        return <SpinnerLoader />
    }

    const tripsTabs = [
        {
            key: "detail",
            label: "General Details",
            icon: activeTab === "detail" ? activeDetailsIcon : detailsIcon,
        },
        {
            key: "history",
            label: "Refuelling History",
            icon: activeTab === "history" ? refuelIcon : refuelIcon,
        }
    ];

    const fuelReconciliationItems = [
        {
            icon: currentSpeedIcon,
            label: "Current Speed",
            unit: "Km/h",
            value: getVehicleDetailsByFuelLogData?.current_info?.speed,
        },
        {
            icon: odoMeterReadingIcon,
            label: "Odometer Reading",
            unit: "km/miles",
            value: getVehicleDetailsByFuelLogData?.current_info?.odm
        },
        {
            icon: lastFuelVolIcon,
            label: "Last Fuel Volume",
            unit: "ltrs",
            value: getVehicleDetailsByFuelLogData?.current_info?.fuel_volume,
        },
        {
            icon: refuelIcon,
            label: "Last Refuel Date",
            // unit: "PKR",
            value: getVehicleDetailsByFuelLogData?.current_info?.refuel_date || "N/A",
        }
    ];

    const currentFuelPercentage = getVehicleDetailsByFuelLogData?.fuel_level;

    return (
        <div>
            <Breadcrumbs values={["Vehicle Details", "General Details"]} />

            <div className="page_header">
                <h2>Vehicle Details</h2>

                <div className="d-flex align-items-center gap-2">
                    <Button
                        cancelBtn={() => {
                            navigate(-1);
                        }}
                        type="button"
                        shouldRow={false}
                        isShowSubmitBtn={false}
                    />
                </div>
            </div>

            <div className="page_container">
                <Row>
                    <Col md={9}>
                        <div className="trip_details_main_div">
                            <div>
                                <h6>
                                    Vehicle ID: <span>{getVehicleDetailsByFuelLogData?.registration_number || "N/A"}</span>
                                </h6>
                            </div>
                            <CustomTabs
                                tabs={tripsTabs}
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                            />

                            {
                                activeTab === "detail" &&
                                <Row>
                                    <Col md={6}>
                                        <FuelCard
                                            title="Vehicle Core Info"
                                            subtitle="Fuel Information for this trip record."
                                            items={fuelReconciliationItems}
                                        />
                                    </Col>
                                    <Col md={6}>
                                        <div className="fuel_card_container">
                                            <div className="fuel_card_header">
                                                <div className="fuel_card_title_group">
                                                    <h4>Fuel Tank Level</h4>
                                                    <p>Fuel tank level for this trip record.</p>
                                                </div>
                                            </div>

                                            <div className="fuel_tank_visual_container">
                                                <img src={images.fuelTruck} alt={imagesAlt.fuelTruck} className="w-100" />
                                                <div className="fuel_overlay_text">
                                                    <h1>{currentFuelPercentage}%</h1>
                                                    <span>Current Fuel in Tank</span>
                                                </div>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={12}>
                                        <div style={{ position: "relative" }}>
                                            <Overlay />
                                            <div className="content_box">
                                                <h6>Trend Visualization</h6>
                                                <p>Fuel consumption and refill patterns across trip duration.</p>

                                                <Row>
                                                    <Col md={6}>
                                                        <div className="chart_main mb-3">
                                                            <h5>Fuel Volume (Liters)</h5>

                                                            <ReactApexChart
                                                                type="line"
                                                                height={320}
                                                                series={[{ name: "Fuel", data: fuelData }]}
                                                                options={{
                                                                    ...baseOptions,
                                                                    colors: ["#F59E0B"],
                                                                    yaxis: {
                                                                        ...baseOptions.yaxis,
                                                                        min: 20,
                                                                        max: 90,
                                                                        tickAmount: 7,
                                                                        labels: {
                                                                            ...baseOptions.yaxis.labels,
                                                                            formatter: (val) => {
                                                                                const v = Math.round(val);
                                                                                return v % 10 === 0 ? `${v}` : "";
                                                                            },
                                                                        },
                                                                        title: { ...baseOptions.yaxis.title, text: "" },
                                                                    },
                                                                }}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="chart_main mb-3">
                                                            <h5>Fuel Efficiency (Km/L)</h5>

                                                            <ReactApexChart
                                                                type="line"
                                                                height={320}
                                                                series={[{ name: "Fuel", data: fuelData }]}
                                                                options={{
                                                                    ...baseOptions,
                                                                    colors: ["#3AD700"],
                                                                    yaxis: {
                                                                        ...baseOptions.yaxis,
                                                                        min: 20,
                                                                        max: 90,
                                                                        tickAmount: 7,
                                                                        labels: {
                                                                            ...baseOptions.yaxis.labels,
                                                                            formatter: (val) => {
                                                                                const v = Math.round(val);
                                                                                return v % 10 === 0 ? `${v}` : "";
                                                                            },
                                                                        },
                                                                        title: { ...baseOptions.yaxis.title, text: "" },
                                                                    },
                                                                }}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="chart_main">
                                                            <h5>Fuel Consumption (Liters)</h5>

                                                            <ReactApexChart
                                                                type="line"
                                                                height={320}
                                                                series={[{ name: "Fuel", data: fuelData }]}
                                                                options={{
                                                                    ...baseOptions,
                                                                    colors: ["#EB4747"],
                                                                    yaxis: {
                                                                        ...baseOptions.yaxis,
                                                                        min: 20,
                                                                        max: 90,
                                                                        tickAmount: 7,
                                                                        labels: {
                                                                            ...baseOptions.yaxis.labels,
                                                                            formatter: (val) => {
                                                                                const v = Math.round(val);
                                                                                return v % 10 === 0 ? `${v}` : "";
                                                                            },
                                                                        },
                                                                        title: { ...baseOptions.yaxis.title, text: "" },
                                                                    },
                                                                }}
                                                            />
                                                        </div>
                                                    </Col>
                                                    <Col md={6}>
                                                        <div className="chart_main">
                                                            <h5>Refuelling Cost (PKR)</h5>

                                                            <ReactApexChart
                                                                type="line"
                                                                height={320}
                                                                series={[{ name: "Fuel", data: fuelData }]}
                                                                options={{
                                                                    ...baseOptions,
                                                                    colors: ["#0146D5"],
                                                                    yaxis: {
                                                                        ...baseOptions.yaxis,
                                                                        min: 20,
                                                                        max: 90,
                                                                        tickAmount: 7,
                                                                        labels: {
                                                                            ...baseOptions.yaxis.labels,
                                                                            formatter: (val) => {
                                                                                const v = Math.round(val);
                                                                                return v % 10 === 0 ? `${v}` : "";
                                                                            },
                                                                        },
                                                                        title: { ...baseOptions.yaxis.title, text: "" },
                                                                    },
                                                                }}
                                                            />
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                            }
                            {
                                activeTab === "history" && <RefuelingHistory />
                            }
                        </div>
                    </Col>
                    <Col md={3}>
                        <VehicleLogsComments />
                    </Col>
                </Row>
            </div>
        </div>
    )
}

export default VehicleDetails